/**
 * Shared chrome for the scheduler panes — the small pieces the list, the
 * editor, the runs panel and the runtime view all draw, so a status chip
 * means the same thing and looks the same wherever it appears, in the
 * workbench and in a downloaded app alike.
 *
 * Colours are the platform CSS variables with light fallbacks, exactly as
 * `RunNavShell` does: a downloaded runtime that never loads `globals.css`
 * still renders legibly.
 */
import type { CSSProperties, ReactNode } from 'react'
import type { RunStatus } from './contract'

export const C = {
  bg: 'var(--color-bg, #f4f6f9)',
  panel: 'var(--color-bg-secondary, #ffffff)',
  s2: 'var(--color-surface-2, #f0f3f7)',
  line: 'var(--color-border, #dde3ea)',
  line2: 'var(--color-border-strong, #c7cfd9)',
  ink: 'var(--color-text, #1c2430)',
  ink2: 'var(--color-text-secondary, #465062)',
  faint: 'var(--color-text-faint, #687281)',
  accent: 'var(--color-accent, #0061D0)',
  accentWeak: 'var(--color-accent-weak, rgba(0,97,208,.10))',
  ok: 'var(--color-success, #1a7f37)',
  okWeak: 'var(--color-success-weak, rgba(26,127,55,.10))',
  err: 'var(--color-error, #cf222e)',
  errWeak: 'var(--color-error-weak, rgba(207,34,46,.10))',
  warn: 'var(--color-warning, #9a6700)',
  warnWeak: 'var(--color-warning-weak, rgba(154,103,0,.10))',
  info: 'var(--color-info, #0969da)',
  infoWeak: 'var(--color-info-weak, rgba(9,105,218,.10))',
}

export const chipStyle: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '2px 9px', borderRadius: 999, fontSize: '0.8125rem',
  fontWeight: 700, lineHeight: 1.5, whiteSpace: 'nowrap',
}

export const labelStyle: CSSProperties = {
  fontSize: 13, fontWeight: 600, letterSpacing: '0.08em',
  textTransform: 'uppercase', color: C.faint,
}

export const cardStyle: CSSProperties = {
  background: C.panel, border: `1px solid ${C.line}`, borderRadius: 10,
}

export const inputStyle: CSSProperties = {
  width: '100%', height: 34, padding: '0 10px', fontSize: '0.9375rem',
  color: C.ink, background: 'var(--color-input-bg, #ffffff)',
  border: `1px solid ${C.line}`, borderRadius: 8,
}

export function Chip({ tone, children }: {
  tone: 'ok' | 'err' | 'warn' | 'info' | 'accent' | 'muted'
  children: ReactNode
}) {
  const tones: Record<string, [string, string]> = {
    ok: [C.okWeak, C.ok],
    err: [C.errWeak, C.err],
    warn: [C.warnWeak, C.warn],
    info: [C.infoWeak, C.info],
    accent: [C.accentWeak, C.accent],
    muted: [C.s2, C.ink2],
  }
  const [bg, fg] = tones[tone] || tones.muted
  return <span style={{ ...chipStyle, background: bg, color: fg }}>{children}</span>
}

const STATUS_TONE: Record<RunStatus, 'ok' | 'err' | 'warn' | 'info' | 'muted'> = {
  succeeded: 'ok',
  failed: 'err',
  running: 'info',
  queued: 'info',
  skipped: 'muted',
  cancelled: 'muted',
}

/**
 * `skipped` is deliberately its own word rather than a kind of failure: "not
 * attempted, because the step before it failed" and "tried and did not work"
 * are different answers to what happened at 2 a.m.
 */
const STATUS_LABEL: Record<RunStatus, string> = {
  succeeded: 'Succeeded',
  failed: 'Failed',
  running: 'Running',
  queued: 'Queued',
  skipped: 'Skipped',
  cancelled: 'Cancelled',
}

export function StatusChip({ status }: { status: RunStatus }) {
  return <Chip tone={STATUS_TONE[status] || 'muted'}>{STATUS_LABEL[status] || status}</Chip>
}

/** "4m 12s" — a duration the way somebody would say it. */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) return '—'
  if (seconds < 1) return '<1s'
  const total = Math.round(seconds)
  const m = Math.floor(total / 60)
  const s = total % 60
  if (m >= 60) {
    const h = Math.floor(m / 60)
    return `${h}h ${m % 60}m`
  }
  return m ? `${m}m ${String(s).padStart(2, '0')}s` : `${s}s`
}

/** A timestamp as a person reads it, in their own browser's zone. */
export function formatWhen(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return String(iso)
  return d.toLocaleString(undefined, {
    weekday: 'short', day: '2-digit', month: 'short',
    hour: '2-digit', minute: '2-digit',
  })
}

/** "in 9h 14m" / "2h ago" — the relative half of a next-run line. */
export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const diff = Math.round((then - Date.now()) / 1000)
  const ahead = diff >= 0
  const mag = Math.abs(diff)
  const d = Math.floor(mag / 86400)
  const h = Math.floor((mag % 86400) / 3600)
  const m = Math.floor((mag % 3600) / 60)
  let text: string
  if (d) text = `${d}d ${h}h`
  else if (h) text = `${h}h ${m}m`
  else text = `${m}m`
  return ahead ? `in ${text}` : `${text} ago`
}

export function Empty({ title, hint }: { title: string; hint?: string }) {
  return (
    <div style={{
      padding: '2.5rem 1rem', textAlign: 'center', color: C.ink2,
      fontSize: '0.9125rem',
    }}>
      <div style={{ fontWeight: 600, color: C.ink, marginBottom: 4 }}>{title}</div>
      {hint && <div style={{ maxWidth: 460, margin: '0 auto' }}>{hint}</div>}
    </div>
  )
}

/**
 * The IOFlow timezone notice. Shown wherever a next run is, because IOFlow
 * stores the timezone and builds its cron trigger without it — so saying
 * nothing would mean showing a time that does not happen.
 */
export function DriftNotice({ drift }: { drift: { message: string } | null }) {
  if (!drift) return null
  return (
    <div style={{
      display: 'flex', gap: 9, padding: '10px 12px', borderRadius: 8,
      fontSize: '0.8425rem', lineHeight: 1.5,
      background: C.warnWeak, border: `1px solid ${C.warn}33`, color: C.warn,
    }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        style={{ flexShrink: 0, marginTop: 1 }}>
        <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
        <path d="M12 9v4M12 17h.01" />
      </svg>
      <div>{drift.message}</div>
    </div>
  )
}
